import { computed, inject, Injectable, signal } from '@angular/core';
import { DataForm } from 'src/app/models/data.model';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { ActivatedRoute, Params } from '@angular/router';
import { RentalPoint } from 'src/app/models/rental-point.model';
import {
  DEFAULT_DIAL_CODE,
  DISCOUNTS,
  ONLINE_PAYMENT_DISCOUNT,
  REVERT_OPENING_DAYS_PARAMS,
  SIMULATE_RENTAL_POINTS_ERROR
} from 'src/app/app.constants';
import { LanguageService } from 'src/app/services/language.service';
import { PlatformLocation } from '@angular/common';
import { logger, Utils } from 'src/app/utils';
import { map, retry, timeout } from 'rxjs';

enum EndpointRoutes {
  GET_RENTAL_POINTS = 'plgn/getRntlPnts',
  GET_RENTAL_POINT_DISCOUNT_CARDS = 'plugin/getRentalPointDiscountCards',
  GET_BIKES_FOR_RENTAL_POINT = 'plugin/getBikesForRentalPointPlugin',
}

@Injectable()
export class FormService {
  private http = inject(HttpClient);
  private route = inject(ActivatedRoute);
  private platformLocation = inject(PlatformLocation);

  private readonly baseHref: string;

  private formSignal = signal(new DataForm());
  readonly form = this.formSignal.asReadonly();

  public rentalPointsSignal = signal<RentalPoint[]>([]);

  readonly startRentalPoints = computed(() => {
    const startIds = Utils.extractIds(this.params['startrentalpoint']);
    return startIds.length > 0
      ? this.rentalPointsSignal().filter((r: any) => startIds.includes(r.accountId))
      : this.rentalPointsSignal();
  });
  readonly endRentalPoints = computed(() => {
    const endIds = Utils.extractIds(this.params['returnrentalpoint']);
    return endIds.length > 0
      ? this.rentalPointsSignal().filter((r: any) => endIds.includes(r.accountId))
      : this.rentalPointsSignal();
  });

  private countriesSignal = signal<any[]>([]);
  readonly countries = this.countriesSignal.asReadonly();

  bikes = signal<any[]>([]);
  accessories = signal<any[]>([]);
  discountCards = signal<any[]>([]);

  accessoriesViewed = signal(false);

  readonly params: any;

  constructor(private languageService: LanguageService) {
    this.baseHref = this.platformLocation.getBaseHrefFromDOM();
    this.baseHref = this.baseHref.endsWith('/') ? this.baseHref.slice(0, -1) : this.baseHref;
    logger.i('baseHref', this.baseHref);
    this.params = {
      ...this.route.snapshot.queryParams,
      dialCode: (this.route.snapshot.queryParams['dialCode'] || DEFAULT_DIAL_CODE).toUpperCase(),
    };
    logger.i('params', this.params);
    this.fetchCountries().subscribe({
      next: (response: any) => this.countriesSignal.set(response),
      error: (error: any) => console.warn('Error fetching countries', error),
    });
  }

  update(data: DataForm) {
    this.formSignal.update(() => {
      data = this.calculatePrice(data);
      data = this.calculateDiscountedPrice(data);

      if (data.isReservation === false) {
        const d = data.discountedPrice || 0;
        data.onlineDiscountedPrice = d - (d * ONLINE_PAYMENT_DISCOUNT / 100);
      } else {
        data.onlineDiscountedPrice = undefined;
      }

      return data;
    });
  }

  private isSameRentalPoint(data: DataForm) {
    const startId = data.startRental?.accountId;
    const endId = data.endRental?.accountId;
    return (!!startId && !!endId) && (startId === endId);
  }

  private calculateItemsPrice(items: any[], priceField: string, quantityField?: string): number {
    return items?.reduce((acc, item) => {
      const quantity = quantityField ? (item[quantityField] || 1) : 1;
      return acc + (item[priceField] * quantity);
    }, 0) || 0;
  }

  private calculateNestedItemsPrice(
    parentItems: any[],
    childrenField: string,
    priceField: string,
    quantityField?: string
  ): number {
    return parentItems?.reduce((acc, parent) => {
      return acc + this.calculateItemsPrice(parent[childrenField], priceField, quantityField);
    }, 0) || 0;
  }

  private calculatePrice(data: DataForm) {
    const daysDuration = data.durationInDays || 1;

    let bikesPrice = this.calculateItemsPrice(data.bikes || [], 'bikePrice');
    let bikeAccessoriesPrice = this.calculateNestedItemsPrice(data.bikes || [], 'accessories', 'bikePrice', 'quantity');
    let accessoriesPrice = this.calculateItemsPrice(data.accessories || [], 'bikePrice', 'quantity');

    bikesPrice *= daysDuration;
    bikeAccessoriesPrice *= daysDuration;
    accessoriesPrice *= daysDuration;

    if (!this.isSameRentalPoint(data)) {
      const bikeCosts = this.calculateItemsPrice(data.bikes || [], 'bikeCost');
      const bikesAccessoriesCostsPrice = this.calculateNestedItemsPrice(data.bikes || [], 'accessories', 'bikeCost');
      const accessoriesCostsPrice = this.calculateItemsPrice(data.accessories || [], 'bikeCost');

      bikesPrice += bikeCosts;
      bikeAccessoriesPrice += bikesAccessoriesCostsPrice;
      accessoriesPrice += accessoriesCostsPrice;

      data.differentRentalPointsPrice = bikeCosts + bikesAccessoriesCostsPrice + accessoriesCostsPrice;
    } else {
      data.differentRentalPointsPrice = 0;
    }

    data.price = bikesPrice + bikeAccessoriesPrice + accessoriesPrice;

    return data;
  }

  private getDailyDiscount(days: number) {
    for (const d of DISCOUNTS) {
      if (d.range.includes('+')) {
        const min = parseInt(d.range);
        if (days >= min) return d.value;
      } else if (d.range.includes('-')) {
        const [min, max] = d.range.split('-').map(Number);
        if (days >= min && days <= max) return d.value;
      }
    }
    return null;
  }

  private applyDailyDiscount(price: number, dailyDiscount: number | null): number {
    if (!dailyDiscount) return price;
    return price - (dailyDiscount / 100 * price);
  }

  private findCardDiscount(item: any, cardDiscountsData: any[]): any {
    if (!cardDiscountsData) return null;
    return cardDiscountsData.find((d: any) => d.bikeTypeId === item.bikeType);
  }

  private applyCardDiscount(price: number, discount: any): number {
    if (!discount || isNaN(discount.price)) return price;
    return price - (discount.price / 100 * price);
  }

  private processItemWithDiscounts(
    item: any,
    daysDuration: number,
    dailyDiscount: number | null,
    cardDiscountsData: any[] | undefined,
    quantityField = 'quantity'
  ): void {
    const quantity = item[quantityField] || 1;
    item.price = item.bikePrice * daysDuration * quantity;

    const priceWithDailyDiscount = this.applyDailyDiscount(item.price, dailyDiscount);

    const cardDiscount = this.findCardDiscount(item, cardDiscountsData || []);
    const priceWithCardDiscount = this.applyCardDiscount(item.price, cardDiscount);

    item.price = Math.min(priceWithDailyDiscount, priceWithCardDiscount);

    // For accessories, calculate the single bike cost
    if (quantityField === 'quantity') {
      item.singleBikeCost = item.price / daysDuration;
    }
  }

  private calculateDiscountedPrice(data: DataForm) {
    const daysDuration = data.durationInDays || 1;
    const dailyDiscount = this.getDailyDiscount(daysDuration);
    const cardDiscountsData = data.discountCard?.discountCardBikeType;

    // Process bikes and their accessories
    if (data.bikes && data.bikes.length > 0) {
      for (const bike of data.bikes) {
        this.processItemWithDiscounts(bike, daysDuration, dailyDiscount, cardDiscountsData);

        // Process bike accessories
        if (bike.accessories && bike.accessories.length > 0) {
          for (const accessory of bike.accessories) {
            this.processItemWithDiscounts(accessory, daysDuration, dailyDiscount, cardDiscountsData);
          }
        }
      }
    }

    // Process extra accessories
    if (data.accessories && data.accessories.length > 0) {
      for (const accessory of data.accessories) {
        this.processItemWithDiscounts(accessory, daysDuration, dailyDiscount, cardDiscountsData);
      }
    }

    // Calculate total prices
    let bikesPrice = this.calculateItemsPrice(data.bikes || [], 'bikePrice');
    let bikeAccessoriesPrice = this.calculateNestedItemsPrice(data.bikes || [], 'accessories', 'bikePrice', 'quantity');
    let accessoriesPrice = this.calculateItemsPrice(data.accessories || [], 'bikePrice', 'quantity');

    // Apply daily discount to total prices
    const bikesPriceWithDailyDiscount = this.applyDailyDiscount(bikesPrice, dailyDiscount);
    const bikeAccessoriesWithDailyDiscount = this.applyDailyDiscount(bikeAccessoriesPrice, dailyDiscount);
    const accessoriesPriceWithDailyDiscount = this.applyDailyDiscount(accessoriesPrice, dailyDiscount);

    // Calculate prices with card discounts
    let bikesPriceWithCardDiscount = bikesPrice;
    let bikeAccessoriesPriceWithCardDiscount = bikeAccessoriesPrice;
    let accessoriesPriceWithCardDiscount = accessoriesPrice;

    if (cardDiscountsData) {
      // Calculate bike prices with card discounts
      bikesPriceWithCardDiscount = (data.bikes || []).reduce((acc, b) => {
        const discount = this.findCardDiscount(b, cardDiscountsData);
        return acc + this.applyCardDiscount(b.bikePrice, discount);
      }, 0) || bikesPrice;

      // Calculate bike accessories prices with card discounts
      bikeAccessoriesPriceWithCardDiscount = (data.bikes || []).reduce((accBike, b) => {
        return accBike + (
          (b.accessories || []).reduce((accAcc, a) => {
            const discount = this.findCardDiscount(a, cardDiscountsData);
            const price = a.bikePrice * (a.quantity || 1);
            return accAcc + this.applyCardDiscount(price, discount);
          }, 0) || 0
        );
      }, 0) || bikeAccessoriesPrice;

      // Calculate extra accessories prices with card discounts
      accessoriesPriceWithCardDiscount = (data.accessories || []).reduce((acc, a) => {
        const discount = this.findCardDiscount(a, cardDiscountsData);
        const price = a.bikePrice * (a.quantity || 1);
        return acc + this.applyCardDiscount(price, discount);
      }, 0) || accessoriesPrice;
    }

    // Take the minimum price between daily discount and card discount
    bikesPrice = Math.min(bikesPriceWithDailyDiscount, bikesPriceWithCardDiscount);
    bikeAccessoriesPrice = Math.min(bikeAccessoriesWithDailyDiscount, bikeAccessoriesPriceWithCardDiscount);
    accessoriesPrice = Math.min(accessoriesPriceWithDailyDiscount, accessoriesPriceWithCardDiscount);

    // Multiply by duration
    bikesPrice *= daysDuration;
    bikeAccessoriesPrice *= daysDuration;
    accessoriesPrice *= daysDuration;

    // Add costs for different rental points
    if (!this.isSameRentalPoint(data)) {
      const bikeCosts = this.calculateItemsPrice(data.bikes || [], 'bikeCost');
      const bikesAccessoriesCostsPrice = this.calculateNestedItemsPrice(data.bikes || [], 'accessories', 'bikeCost');
      const accessoriesCostsPrice = this.calculateItemsPrice(data.accessories || [], 'bikeCost');

      bikesPrice += bikeCosts;
      bikeAccessoriesPrice += bikesAccessoriesCostsPrice;
      accessoriesPrice += accessoriesCostsPrice;
    }

    // Set the final discounted price
    data.discountedPrice = bikesPrice + bikeAccessoriesPrice + accessoriesPrice;

    return data;
  }

  public fetchRentalPoints(params: Params) {
    const p = {
      countryId: params['country'],
      districtId: params['district'],
      isHotel: params['isHotel'],
      startRentalPointAccountIds: params['startrentalpoint']?.split(',').map(Number).filter((v: any) => !isNaN(v)),
      endRentalPointAccountIds: params['endrentalpoint']?.split(',').map(Number).filter((v: any) => !isNaN(v)),
    };
    if (!p.countryId) delete p.countryId;
    if (!p.districtId) delete p.districtId;
    if (!p.isHotel) delete p.isHotel;
    if (!p.startRentalPointAccountIds) delete p.startRentalPointAccountIds;
    if (!p.endRentalPointAccountIds) delete p.endRentalPointAccountIds;

    const url = environment.local && SIMULATE_RENTAL_POINTS_ERROR
      ? 'http://client-error-test.com/api/fail'
      : environment.serverUrl + EndpointRoutes.GET_RENTAL_POINTS;

    return this.http.get(url, { params: p }).pipe(
      timeout(15000),
      retry({ count: 2, delay: 1000 }),
      map((response: any) => response
        .filter((r: any) => r.accountId && r.longitude && r.latitude)
        .map((r: any) => {
          if (REVERT_OPENING_DAYS_PARAMS) {
            r.schedule.openingDays = r.schedule.openingDays
              .replace(/[01]/g, (c: string) => (c === '1' ? '0' : '1'));
          }
          return {
            ...r,
            schedule: !r.schedule ? undefined : {
              ...r.schedule,
              openingDays: Utils.getDays(r.schedule.openingDays),
              scheduleExceptions: !r.schedule?.scheduleExceptions ? undefined :
                r.schedule.scheduleExceptions.map((se: any) => ({
                    ...se,
                    startDay: new Date(se.startDay),
                    endDay: new Date(se.endDay),
                    openingDays: Utils.getDays(se.closingException
                      ? se.openingDays.replace(/[01]/g, (c: string) => (c === '1' ? '0' : '1'))
                      : se.openingDays
                    ),
                  }),
                ),
            },
          };
        })
      )
    );
  }

  private fetchCountries() {
    return this.http.get(`${this.baseHref}/countries.json`);
  }

  public fetchDiscountCards() {
    const params: any = {rentalPointId: this.form().startRental?.accountId};
    return this.http.get(environment.serverUrl + EndpointRoutes.GET_RENTAL_POINT_DISCOUNT_CARDS, {
      params: params,
    });
  }

  /**
   *
   * @startDate must have format YYYY-MM-DD
   * @endDate must have format YYYY-MM-DD
   * */
  public fetchBikesForRentalPoint() {
    const { startRental, from, to } = this.form();
    const params: any = {
      rentalPointId: startRental?.accountId,
      startDate: Utils.toServerDateFormat(from),
      endDate: Utils.toServerDateFormat(to),
    };
    return this.http.get(environment.serverUrl + EndpointRoutes.GET_BIKES_FOR_RENTAL_POINT, { params: params })
      .pipe(
        map((response: any) => {
          const products = response.products || [];
          let bikes = products.filter((p: any) => p.productType === 1) || [];
          const accessories = products.filter((p: any) => p.productType === 2) || [];
          bikes = bikes.map((b: any) => {
            return {
              ...b,
              supportedAccessories: b.accessories?.map((a: any) => ({
                ...a,
                ...accessories.find((ac: any) => ac.bikeType === a.bikeType),
              })) || [],
            }
          });

          const accessoriesInBikes = Array.from(new Set(bikes
            .filter((b: any) => b.supportedAccessories?.length > 0)
            .map((b: any) => b.supportedAccessories.map((a: any) => a.bikeType))
            .flat(1)));

          return {
            ...response,
            products: {
              bikes: bikes.map((b: any) => ({
                ...b,
                availability: b.quantity,
                quantity: null,
                bikePrice: b.price,
                price: 0,
                supportedAccessories: b.supportedAccessories.map((a: any) => ({
                  ...a,
                  availability: a.quantity,
                  quantity: null,
                  bikePrice: a.price,
                  price: 0,
                  singleBikeCost: 0
                })),
                accessories: [],
              })),
              accessories: accessories.filter((a: any) => !accessoriesInBikes.includes(a.bikeType)).map((a: any) => ({
                ...a,
                availability: a.quantity,
                quantity: null,
                bikePrice: a.price,
                price: 0,
                singleBikeCost: 0,
              })),
            }
          }
        })
      );
  }

}
